import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = ({ language = 'fr' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay (not on first visit)
      const hasVisited = localStorage.getItem('sadaskincare_visited');
      if (hasVisited) {
        setTimeout(() => setShowPrompt(true), 3000);
      } else {
        localStorage.setItem('sadaskincare_visited', 'true');
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt
    if (iOS) {
      const hasVisited = localStorage.getItem('sadaskincare_visited');
      const dismissed = localStorage.getItem('sadaskincare_pwa_dismissed');
      if (hasVisited && !dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      } else if (!hasVisited) {
        localStorage.setItem('sadaskincare_visited', 'true');
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('sadaskincare_pwa_dismissed', 'true');
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-in slide-in-from-bottom-5">
      <div className="bg-white rounded-2xl shadow-2xl border border-[#E7E5E4] p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-[#78716C] hover:text-[#0F172A]"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-[#0F172A] mb-1">
              {language === 'fr' ? 'Installer SADASkinCare' : 'Install SADASkinCare'}
            </h3>
            <p className="text-sm text-[#78716C] mb-3">
              {language === 'fr' 
                ? 'Accédez rapidement à l\'app depuis votre écran d\'accueil'
                : 'Quick access from your home screen'
              }
            </p>

            {isIOS ? (
              <div className="text-xs text-[#78716C] bg-[#F5F5F4] rounded-lg p-3">
                <p className="font-medium mb-2">
                  {language === 'fr' ? 'Sur Safari :' : 'On Safari:'}
                </p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>{language === 'fr' ? 'Appuyez sur' : 'Tap'} <span className="inline-block">⬆️</span> {language === 'fr' ? '(Partager)' : '(Share)'}</li>
                  <li>{language === 'fr' ? 'Sélectionnez "Sur l\'écran d\'accueil"' : 'Select "Add to Home Screen"'}</li>
                </ol>
              </div>
            ) : (
              <Button 
                onClick={handleInstall}
                className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Installer' : 'Install'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
